import React, { useMemo, useState, useCallback } from "react";
import { GoogleMap, InfoWindow, OverlayView, OverlayViewF, useJsApiLoader } from "@react-google-maps/api";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";

const GOOGLE_LIBRARIES = ["places"];
const CONTAINER_STYLE = { width: "100%", height: "calc(100vh - 260px)", minHeight: "460px", borderRadius: "20px" };
const DEFAULT_CENTER = { lat: 25.2138, lng: 75.8648 }; // Kota
const imgUrl = (path) => path && (path.startsWith("http") ? path : `${process.env.REACT_APP_BACKEND_URL}${path}`);

// "₹6k/mo" style tag — min monthly room-type price, else per-night bed price.
const priceTag = (h) => {
  const monthly = (h.room_types || []).map((rt) => Number(rt.price_per_month)).filter((n) => n > 0);
  if (monthly.length) {
    const m = Math.min(...monthly);
    const short = m >= 1000 ? `${+(m / 1000).toFixed(1)}k` : m;
    return { label: `₹${short}/mo`, amount: m, unit: "/month" };
  }
  return { label: `₹${Number(h.price_per_bed).toLocaleString("en-IN")}/nt`, amount: h.price_per_bed, unit: "/night" };
};

/**
 * Airbnb-style "all hostels on one map" view for /hostels.
 *
 * Props: { hostels: [] }
 * Silently skips hostels that don't yet have map coordinates — a small hint
 * shows in the corner if any listings are missing pins.
 */
export default function HostelBrowseMap({ hostels }) {
  const [activeId, setActiveId] = useState(null);
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-maps-loader",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: GOOGLE_LIBRARIES,
  });

  const pinned = useMemo(
    () => (hostels || []).filter((h) => Number.isFinite(Number(h.map_lat)) && Number.isFinite(Number(h.map_lng)) && (Number(h.map_lat) !== 0 || Number(h.map_lng) !== 0)),
    [hostels],
  );
  const missing = (hostels || []).length - pinned.length;

  // Auto-fit map to include every pinned hostel — falls back to Kota if none.
  const initialBounds = useMemo(() => {
    if (pinned.length === 0) return null;
    return pinned.reduce(
      (acc, h) => ({
        minLat: Math.min(acc.minLat, Number(h.map_lat)),
        maxLat: Math.max(acc.maxLat, Number(h.map_lat)),
        minLng: Math.min(acc.minLng, Number(h.map_lng)),
        maxLng: Math.max(acc.maxLng, Number(h.map_lng)),
      }),
      { minLat: 90, maxLat: -90, minLng: 180, maxLng: -180 },
    );
  }, [pinned]);

  const initialCenter = initialBounds
    ? { lat: (initialBounds.minLat + initialBounds.maxLat) / 2, lng: (initialBounds.minLng + initialBounds.maxLng) / 2 }
    : DEFAULT_CENTER;

  const onMapLoad = useCallback((map) => {
    if (pinned.length > 1 && initialBounds && window.google) {
      const b = new window.google.maps.LatLngBounds(
        { lat: initialBounds.minLat, lng: initialBounds.minLng },
        { lat: initialBounds.maxLat, lng: initialBounds.maxLng },
      );
      map.fitBounds(b, 60);
    } else if (pinned.length === 1) {
      map.setZoom(15);
    }
  }, [pinned, initialBounds]);

  if (loadError) {
    return (
      <div className="rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 p-6 text-sm">
        Google Maps could not be loaded. Please refresh and try again.
      </div>
    );
  }
  if (!isLoaded) {
    return <div className="rounded-2xl bg-slate-100 animate-pulse" style={{ height: "calc(100vh - 260px)", minHeight: 460 }} />;
  }

  if (pinned.length === 0) {
    return (
      <div className="rounded-2xl bg-white border border-dashed border-slate-300 p-10 text-center text-slate-500">
        <MapPin className="mx-auto text-orange-800/60 mb-3" size={32} />
        <div className="font-semibold text-slate-700">No listings have location pins yet.</div>
        <div className="text-sm mt-1">Ask hostel owners to set their exact map location so students can browse visually.</div>
      </div>
    );
  }

  const active = pinned.find((h) => h.id === activeId);

  return (
    <div className="relative" data-testid="browse-map-view">
      <GoogleMap
        mapContainerStyle={CONTAINER_STYLE}
        center={initialCenter}
        zoom={12}
        onLoad={onMapLoad}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          gestureHandling: "greedy",
          clickableIcons: false,
        }}
      >
        {pinned.map((h) => (
          <OverlayViewF
            key={h.id}
            position={{ lat: Number(h.map_lat), lng: Number(h.map_lng) }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={(w, ht) => ({ x: -w / 2, y: -(ht || 0) - 8 })}
          >
            <button
              type="button"
              data-testid={`map-price-pin-${h.id}`}
              title={`${h.name} — ${priceTag(h).label}`}
              onClick={(e) => { e.stopPropagation(); setActiveId(h.id); }}
              className={`relative whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-black shadow-md border transition-transform hover:scale-110 ${
                activeId === h.id ? "bg-stone-900 text-white border-stone-900 scale-110 z-20" : "bg-white text-stone-900 border-slate-300"
              }`}
            >
              {priceTag(h).label}
              <span className={`absolute left-1/2 -bottom-[5px] -translate-x-1/2 w-2.5 h-2.5 rotate-45 border-b border-r ${
                activeId === h.id ? "bg-stone-900 border-stone-900" : "bg-white border-slate-300"
              }`} aria-hidden />
            </button>
          </OverlayViewF>
        ))}
        {active && (
          <InfoWindow
            position={{ lat: Number(active.map_lat), lng: Number(active.map_lng) }}
            onCloseClick={() => setActiveId(null)}
          >
            <div className="w-48" data-testid={`map-info-${active.id}`}>
              {active.images?.[0] && (
                <img
                  src={imgUrl(active.images[0])}
                  alt={active.name}
                  className="w-full h-24 object-cover rounded-md mb-1.5"
                  loading="lazy"
                />
              )}
              <div className="font-bold text-sm text-slate-900 truncate">{active.name}</div>
              <div className="text-[11px] text-slate-500 truncate">{active.city} · {active.gender}</div>
              <div className="mt-1 flex items-center justify-between gap-2">
                <div className="text-sm font-black text-orange-800">₹{Number(priceTag(active).amount).toLocaleString("en-IN")}<span className="text-[10px] text-slate-500 font-normal">{priceTag(active).unit}</span></div>
                <Link
                  to={`/hostels/${active.id}`}
                  data-testid={`map-info-view-${active.id}`}
                  className="text-xs font-semibold text-orange-800 hover:underline"
                >
                  View →
                </Link>
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
      {missing > 0 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 rounded-full bg-stone-900/90 text-white text-[11px] px-3 py-1.5 shadow-lg pointer-events-none">
          {missing} listing{missing === 1 ? "" : "s"} not shown — owner{missing === 1 ? "" : "s"} haven't set a map location yet
        </div>
      )}
    </div>
  );
}
